import React, { useEffect, useRef } from 'react'

type Props = {
  embedId: string
  embedToken: string
  viewerId?: string
}

const SCRIPT_URL = 'https://embed.launchnotes.io/embed_v2/latest/embed.js'

const LaunchNotes: React.FC<Props> = ({ embedId, embedToken, viewerId }) => {
  const loadAttempts = useRef(0)
  const maxAttempts = 3

  useEffect(() => {
    let mounted = true
    const timeouts: NodeJS.Timeout[] = []

    const loadScript = () => {
      if (document.querySelector(`script[src="${SCRIPT_URL}"]`)) {
        // Script already loaded (e.g. hot-reload). Re-initialize the embed
        // since DOMContentLoaded won't fire again.
        if (window.launchnotesEmbed?.initialize) {
          window.launchnotesEmbed.initialize()
        }
        return
      }

      const script = document.createElement('script')
      script.src = SCRIPT_URL
      script.async = true
      script.onload = () => {
        // The script auto-initializes on DOMContentLoaded, but in a React
        // SPA that event has already fired before this component mounts.
        // Manually call initialize() so the embed finds our custom element.
        if (mounted && window.launchnotesEmbed?.initialize) {
          window.launchnotesEmbed.initialize()
        }
      }
      script.onerror = () => {
        if (mounted && loadAttempts.current < maxAttempts) {
          loadAttempts.current++
          script.remove()
          const timeout = setTimeout(loadScript, 2000 * loadAttempts.current)
          timeouts.push(timeout)
        }
      }
      if (document.body) document.body.appendChild(script)
    }

    loadScript()

    return () => {
      mounted = false
      timeouts.forEach(clearTimeout)
    }
  }, [])

  return (
    <launchnotes-embed
      data-id={embedId}
      data-token={embedToken}
      data-embed-type="popup"
      data-target="ln-embed"
      data-show-unread="true"
      data-unread-placement="top-end"
      {...(viewerId ? { 'data-viewer-id': viewerId } : {})}
    />
  )
}

export default LaunchNotes
